/**
 * Multi-step wizard form component generator
 */

import {
  getAllFormSchemaFields,
  getStepFieldNames
} from '@core-engine/schema/field-helpers/index.js'
import type { FormSchema, GeneratorOptions } from '@core-engine/schema/types.js'
import { toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import {
  buildDefaultValues,
  buildFieldArrayDecls,
  buildFieldErrorHelper,
  buildWatchDecls,
  buildZodFields,
  escapeJsx,
  getListFields,
  type PublicFormComponentSourceOptions,
  renderFieldsJSX,
  type StepResult
} from '@next-adapter/generators/form-pipeline/form-component-shared.js'

export function generateMultiStepForm(
  _schema: FormSchema,
  _cwd: string,
  _adminDir: string,
  _options: GeneratorOptions
): StepResult {
  return { files: [] }
}

export function buildMultiStepFormComponentSource(
  schema: FormSchema,
  options: PublicFormComponentSourceOptions = {}
): string {
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const actionImportPath =
    options.actionImportPath ?? resolveSchemaActionImportPath(formName, options.outputNamespace)
  const steps = schema.steps!

  // All fields flattened (for Zod schema + defaults)
  const allFields = getAllFormSchemaFields(schema)
  const zodFields = buildZodFields(allFields)
  const defaults = buildDefaultValues(allFields)

  // List fields and watch declarations are global
  const listFields = getListFields(allFields)
  const hasListFields = listFields.length > 0
  const { setup: watchSetup } = buildWatchDecls(allFields)

  const rhfImport = hasListFields
    ? `import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form'`
    : `import { Controller, useForm, type FieldErrors } from 'react-hook-form'`
  const fieldArraySetup = hasListFields ? `\n${buildFieldArrayDecls(listFields)}\n` : ''

  // Build STEPS constant
  const stepsConst = buildStepsConstant(steps)

  // Build per-step content blocks
  const stepContentBlocks = steps
    .map((step, index) => {
      const fieldsJSX = renderFieldsJSX(step.fields)
      return `          {currentStep === ${index} && (\n            <>\n${fieldsJSX}\n            </>\n          )}`
    })
    .join('\n')

  const submitText = JSON.stringify(schema.submitButtonText || 'Submit')
  const successMessage = escapeJsx(schema.successMessage || 'Form submitted successfully!')
  const redirectUrl = schema.redirectUrl
  const successHandler = redirectUrl
    ? `window.location.href = ${JSON.stringify(redirectUrl)}`
    : 'setSubmitted(true)'

  const content = buildComponentSource({
    pascal,
    rhfImport,
    hasListFields,
    actionImportPath,
    zodFields,
    defaults,
    stepsConst,
    fieldArraySetup,
    watchSetup,
    stepContentBlocks,
    submitText,
    successMessage,
    successHandler
  })

  return content
}

function buildStepsConstant(steps: NonNullable<FormSchema['steps']>): string {
  const entries = steps.map((step) => {
    const fieldNames = getStepFieldNames(step)
    const fieldsStr = fieldNames.map((n) => JSON.stringify(n)).join(', ')
    const desc = step.description ? `, description: ${JSON.stringify(step.description)}` : ''
    return `  { name: ${JSON.stringify(step.name)}, label: ${JSON.stringify(step.label)}${desc}, fields: [${fieldsStr}] }`
  })
  return `const STEPS = [\n${entries.join(',\n')}\n]`
}

interface ComponentParts {
  pascal: string
  actionImportPath: string
  rhfImport: string
  hasListFields: boolean
  zodFields: string
  defaults: string
  stepsConst: string
  fieldArraySetup: string
  watchSetup: string
  stepContentBlocks: string
  submitText: string
  successMessage: string
  successHandler: string
}

function buildComponentSource(p: ComponentParts): string {
  const providerOpen = `    <NuqsAdapter>
      <React.Suspense fallback={null}>
        <${p.pascal}FormInner />
      </React.Suspense>
    </NuqsAdapter>`
  const exportWrapper = `\nexport function ${p.pascal}Form() {
  return (
${providerOpen}
  )
}\n`

  return `'use client'

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { ChevronLeft, ChevronRight${p.hasListFields ? ', Trash2' : ''} } from 'lucide-react'
import { createParser, useQueryState } from 'nuqs'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import * as React from 'react'
${p.rhfImport}
import { z } from 'zod/v3'
import { create${p.pascal}Submission } from '@admin/actions/${p.actionImportPath}'

const formSchema = z.object({
${p.zodFields}
})

type FormValues = z.infer<typeof formSchema>
${buildFieldErrorHelper()}

${p.stepsConst}

const stepParser = createParser({
  parse(value) {
    if (!/^\\d+$/.test(value)) {
      return null
    }
    const step = Number.parseInt(value, 10)
    if (!Number.isInteger(step) || step < 0 || step >= STEPS.length) {
      return null
    }
    return step
  },
  serialize(value) {
    return String(value)
  }
}).withDefault(0)

function ${p.pascal}FormInner() {
  const [currentStep, setCurrentStep] = useQueryState('step', stepParser)
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, startSubmitTransition] = React.useTransition()

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
${p.defaults}
    },
  })

${p.fieldArraySetup}${p.watchSetup}
  async function handleNext() {
    const stepFields = STEPS[currentStep].fields as (keyof FormValues)[]
    const isValid = await form.trigger(stepFields, { shouldFocus: true })
    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  function handleBack() {
    setCurrentStep(currentStep - 1)
  }

  function onSubmit(values: FormValues) {
    startSubmitTransition(async () => {
      try {
        const result = await create${p.pascal}Submission(values)
        if (result.success) {
          ${p.successHandler}
        } else {
          form.setError('root', { message: result.error || 'Something went wrong' })
        }
      } catch {
        form.setError('root', { message: 'Something went wrong. Please try again.' })
      }
    })
  }

  if (submitted) {
    return (
      <div className="rounded-sm border p-6 text-center">
        <h3 className="text-lg font-semibold">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">${p.successMessage}</p>
      </div>
    )
  }

  return (
    <form onSubmit={(event) => event.preventDefault()} className="space-y-8">
        {/* Step header */}
        <div>
          <h3 className="text-lg font-semibold">{STEPS[currentStep].label}</h3>
          {'description' in STEPS[currentStep] && STEPS[currentStep].description && (
            <p className="text-sm text-muted-foreground mt-1">{STEPS[currentStep].description}</p>
          )}
        </div>

        {/* Step content */}
        <div key={currentStep} className="animate-in fade-in duration-300 space-y-6">
${p.stepContentBlocks}
        </div>

        {form.formState.errors.root && (
          <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronLeft className="size-4" />
            Back
          </button>
          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              Next
              <ChevronRight className="ml-2 size-4" />
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={() => form.handleSubmit(onSubmit)()}
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : ${p.submitText}}
            </button>
          )}
        </div>
      </form>
  )
}
${exportWrapper}`
}
